import { useCallback, useMemo, useState } from "react"

import { Alert, Button, Col, Container, Form, Modal, Row, Tab, Tabs } from "react-bootstrap"
import { FaRegBell, FaRegEnvelope } from "react-icons/fa"

import NotificationApplicationUserInfo from "./NotificationApplicationUserInfo"
import NotificationApplicationUsersGrid from "./NotificationApplicationUsersGrid"
import NotificationEditTabTitle from "./NotificationEditTabTitle"
import NotificationEventsTab from "./NotificationEventsTab"
import NotificationMethodsTab from "./NotificationMethodsTab"
import NotificationApplicationUserData from "../../../api/Randoms/Notification/NotificationApplicationUserData"
import NotificationContactData from "../../../api/Randoms/Notification/NotificationContactData"
import NotificationEvent from "../../../api/Randoms/Notification/NotificationEvent"
import NotificationMode, { NotificationModes } from "../../../api/Randoms/Notification/NotificationMode"
import { useNotificationContacts } from "../../../api/Randoms/Notification/useNotificationContacts"
import { useNotificationContactsActions } from "../../../api/Randoms/Notification/useNotificationContactsActions"
import useNotificationContactSave from "../../../api/Randoms/Notification/useNotificationContactSave"
import useNotificationEvents from "../../../api/Randoms/Notification/useNotificationEvents"
import useNotificationMethods from "../../../api/Randoms/Notification/useNotificationMethods"
import ActionButton from "../../ActionButton"

interface NotificationContactEditModalProps {
  contact?: NotificationContactData | null | undefined
  onHide?: () => void
}

enum TabKey {
  METHODS = "methods",
  EVENTS = "events",
}

function NotificationContactEditModal({ contact = undefined, onHide = undefined }: NotificationContactEditModalProps) {
  const { poolId } = useNotificationContacts()
  const { reloadContacts } = useNotificationContactsActions()

  const initialContact = useMemo(
    () =>
      contact || {
        PoolID: poolId,
        ContactID: 0,
        ApplicationUserID: 0,
        UserName: "",
        Email: "",
        FirstName: "",
        LastName: "",
        Phone: "",
        NotificationMode: NotificationMode.AUTOMATIC,
        NotificationMethods: [],
        NotificationEvents: [],
      },
    [contact, poolId]
  )

  const [notificationContact, setNotificationContact] = useState(initialContact)

  const {
    ContactID,
    ApplicationUserID,
    UserName,
    Email,
    FirstName,
    LastName,
    Phone,
    NotificationMode: Mode,
    NotificationMethods: Methods,
    NotificationEvents: Events,
  } = notificationContact

  const [loadingMethods, methods, setMethods, resetMethodsSelection] = useNotificationMethods(Methods)
  const [loadingEvents, events, setEvents, resetEventsSelection] = useNotificationEvents(Events)
  const [tabKey, setTabKey] = useState(Mode === NotificationMode.MANUAL ? TabKey.METHODS : TabKey.EVENTS)
  const [showAlert, setShowAlert] = useState(false)

  const onContactSavedSuccessfully = useCallback(() => {
    reloadContacts()
    if (!loadingMethods && !loadingEvents) {
      onHide?.()
    }
  }, [onHide, reloadContacts, loadingMethods, loadingEvents])

  const { saving: savingContact, submitContact } = useNotificationContactSave<string>(onContactSavedSuccessfully)

  const isMethodsValid = () => {
    return Object.values(methods).some((m) => m.selected)
  }

  const isEventsValid = () => {
    return (
      Mode === NotificationMode.MANUAL ||
      (Mode === NotificationMode.AUTOMATIC && Object.values(events).some((e) => e.selected))
    )
  }
  const isFormValid = () => {
    return ApplicationUserID > 0 && isMethodsValid() && isEventsValid()
  }

  const shouldShowAlert = () => {
    return ApplicationUserID === 0 || !isMethodsValid() || !isEventsValid()
  }

  const handleSaveContact = (event: React.FormEvent) => {
    event.preventDefault()
    if (isFormValid()) {
      submitContact({
        ...notificationContact,
        NotificationMethods: Object.values(methods),
        NotificationEvents: Object.values(events),
      })
    } else {
      setShowAlert(shouldShowAlert)
    }
  }

  const handleSelectApplicationUser = useCallback(
    (user: NotificationApplicationUserData | null) => {
      setNotificationContact((prev) => ({
        ...prev,
        ...(user ?? initialContact),
      }))
      if (!user) {
        resetEventsSelection()
        resetMethodsSelection()
      }
    },
    [initialContact, resetEventsSelection, resetMethodsSelection]
  )

  const handleChangeNotificationMethods = useCallback(
    (code: string, checked: boolean) => {
      setMethods((prev) => ({ ...prev, [code]: { ...prev[code], selected: checked } }))
      setShowAlert(false)
    },
    [setMethods]
  )

  const handleChangeNotificationEvents = useCallback(
    (code: string, checked: boolean) => {
      setEvents((prev) => ({ ...prev, [code]: { ...prev[code], selected: checked } }))
      setShowAlert(false)
    },
    [setEvents]
  )

  const handleChangeNotificationMode = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const notificationModeValue = Number(event.target.value)
      setNotificationContact((prev) => {
        if (prev.NotificationMode !== notificationModeValue) {
          return {
            ...prev,
            NotificationMode: notificationModeValue,
            NotificationEvents:
              notificationModeValue === NotificationMode.MANUAL ? [events.MANUAL_ROSTER_REMINDER] : [],
          }
        }
        return prev
      })

      if (notificationModeValue === NotificationMode.MANUAL) {
        // Switch to the Notification Methods tab.
        setTabKey(TabKey.METHODS)

        // Reset selected status for events without using Object.fromEntries
        const newEvents = Object.entries(events).reduce((acc: Record<string, NotificationEvent>, [key, value]) => {
          acc[key] = { ...value, selected: false }
          return acc
        }, {})

        setEvents(newEvents)
      } else if (notificationModeValue === NotificationMode.AUTOMATIC) {
        // Switch to the Notification Methods tab.
        setTabKey(TabKey.EVENTS)
      }
    },
    [events, setEvents]
  )

  return (
    <Modal backdrop="static" size={ContactID === 0 ? "xl" : "lg"} centered show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="text-uppercase h5">{`${ContactID ? "Edit" : "Add"} Notification Contact`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" transition show={showAlert} onClose={() => setShowAlert(false)} dismissible>
          <ul className="mb-0 pl-3">
            {notificationContact.ApplicationUserID === 0 && <li>User must be selected.</li>}
            {Object.values(methods).every((m) => !m.selected) && (
              <li>
                At least one <span className="text-uppercase small">notification method</span> must be selected.
              </li>
            )}
            {Mode === NotificationMode.AUTOMATIC && Object.values(events).every((e) => !e.selected) && (
              <li>
                At least one <span className="text-uppercase small">reminder</span> must be selected.
              </li>
            )}
          </ul>
        </Alert>
        {ContactID === 0 && (
          <NotificationApplicationUsersGrid poolId={poolId} onSelectUser={handleSelectApplicationUser} />
        )}
        {ContactID > 0 && (
          <NotificationApplicationUserInfo
            UserName={UserName}
            FirstName={FirstName}
            LastName={LastName}
            Email={Email}
            Phone={Phone}
          />
        )}
        <Form.Group as={Row} className="mt-3">
          <Form.Label column sm={3}>
            Notification Mode
          </Form.Label>
          <Col className="d-flex align-items-center">
            {NotificationModes.map((mode) => {
              const modeValue = NotificationMode[mode]
              return (
                <Form.Check
                  key={`nm-${mode}`}
                  inline
                  type="radio"
                  label={mode}
                  name="notification-mode"
                  id={`nm-${mode}`}
                  className="abc-radio small"
                  value={modeValue}
                  onChange={handleChangeNotificationMode}
                  checked={Mode === NotificationMode[mode]}
                />
              )
            })}
          </Col>
        </Form.Group>
        <Container className="tab-material">
          <Tabs activeKey={tabKey} onSelect={(k) => k && setTabKey(TabKey[k.toUpperCase() as keyof typeof TabKey])}>
            {Mode === NotificationMode.AUTOMATIC && (
              <Tab
                eventKey={TabKey.EVENTS}
                title={
                  <NotificationEditTabTitle
                    items={Object.values(events || []).filter((e) => e.DisplayOrder > 0)}
                    title="Reminders"
                    icon={<FaRegBell className="small" />}
                  />
                }
                disabled={Mode !== NotificationMode.AUTOMATIC}
              >
                <NotificationEventsTab
                  loading={loadingEvents}
                  events={events}
                  onChange={handleChangeNotificationEvents}
                  disabled={ApplicationUserID === 0}
                />
              </Tab>
            )}
            <Tab
              eventKey={TabKey.METHODS}
              title={
                <NotificationEditTabTitle
                  items={methods}
                  title="Notification Methods"
                  icon={<FaRegEnvelope className="small" />}
                />
              }
            >
              <NotificationMethodsTab
                loading={loadingMethods}
                methods={methods}
                contact={notificationContact}
                onChange={handleChangeNotificationMethods}
              />
            </Tab>
          </Tabs>
        </Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={savingContact || loadingMethods || loadingEvents} onClick={onHide}>
          Cancel
        </Button>
        <ActionButton
          variant="primary"
          onClick={handleSaveContact}
          disabled={loadingMethods || loadingEvents}
          spin={savingContact}
          disableOnSpin
        >
          Submit
        </ActionButton>
      </Modal.Footer>
    </Modal>
  )
}

export default NotificationContactEditModal
